/* eslint-disable */
import React from "react";

/** Normalize for robust matching (case/accents/punct/extra spaces). */
function norm(s: string): string {
  return s
    ?.toLowerCase()
    ?.normalize("NFD")
    ?.replace(/\p{M}+/gu, "") // strip diacritics
    ?.replace(/[^\p{L}\p{N}\s]+/gu, " ") // drop punctuation
    ?.replace(/\s+/g, " ")
    ?.trim();
}

function tokens(s: string): string[] {
  const n = norm(s);
  return n ? n.split(" ") : [];
}

/** Sørensen–Dice similarity on character bigrams (good for partials). */
function diceBigrams(a: string, b: string): number {
  const A = norm(a)?.replace(/\s+/g, "");
  const B = norm(b)?.replace(/\s+/g, "");
  if (!A?.length || !B?.length) return 0;

  const bigrams = (t: string) => {
    const arr: string[] = [];
    for (let i = 0; i < t?.length - 1; i++) arr.push(t?.slice(i, i + 2));
    return arr;
  };

  const a2 = bigrams(A);
  const b2 = bigrams(B);
  if (!a2?.length || !b2?.length) return 0;

  const map = new Map<string, number>();
  for (const g of a2) map.set(g, (map.get(g) ?? 0) + 1);

  let inter = 0;
  for (const g of b2) {
    const c = map.get(g) ?? 0;
    if (c > 0) {
      inter++;
      map.set(g, c - 1);
    }
  }
  return (2 * inter) / (a2?.length + b2?.length);
}

/** Token overlap: coverage (recall) + Jaccard (set overlap). */
function tokenOverlap(a: string, b: string): {
  coverage: number; // fraction of query tokens found in text
  jaccard: number; // |A∩B| / |A∪B|
  common: Set<string>;
} {
  const A = new Set(tokens(a));
  const B = new Set(tokens(b));
  if (!A?.size || !B?.size) return { coverage: 0, jaccard: 0, common: new Set() };

  let inter = 0;
  const common = new Set<string>();
  for (const t of A) if (B.has(t)) { inter++; common.add(t); }

  const coverage = inter / A?.size;
  const jaccard = inter / (A.size + B.size - inter);
  return { coverage, jaccard, common };
}

/** Combined similarity 0..100. Tuned for “missing word / near-duplicate” names. */
export function fuzzySimilarity(text: string, query: string): {
  score: number; // 0..100
  details: { dice: number; coverage: number; jaccard: number; prefixBonus: number };
  matchedTokens: string[];
} {
  if (!query?.trim()) return { score: 0, details: { dice: 0, coverage: 0, jaccard: 0, prefixBonus: 0 }, matchedTokens: [] };

  const { coverage, jaccard, common } = tokenOverlap(query, text);
  const dice = diceBigrams(query, text);

  // Helpful bump if text starts with most of the query (brand prefix, etc.)
  const nq = norm(query);
  const nt = norm(text);
  const prefixBonus = nt?.startsWith(nq?.split(" ").slice(0, 2).join(" ")) ? 0.06 : 0; // up to +6%

  // Weighted blend -> 0..100
  const score =
    Math.round(
      100 *
        Math.min(
          1,
          (0.55 * dice) + // char-level softness
          (0.30 * coverage) + // query tokens recall
          (0.15 * jaccard) + // set overlap balance
          prefixBonus
        )
    );

  return { score, details: { dice, coverage, jaccard, prefixBonus }, matchedTokens: Array.from(common) };
}

/** Filter + rank everything above a threshold (default 50). */
export function fuzzyFilterSimilarity<T>(
  items: T[],
  query: string,
  getText: (item: T) => string,
  opts: { threshold?: number; limit?: number } = {}
): Array<{ item: T; score: number; matchedTokens: string[] }> {
  const threshold = opts.threshold ?? 50;
  const limit = opts.limit ?? 200;

  const scored = items
    .map((item) => {
      const text = getText(item);
      const { score, matchedTokens } = fuzzySimilarity(text, query);
      return { item, score, matchedTokens };
    })
    .sort((a, b) => b.score - a.score)
    .filter((r) => r.score >= threshold);

  return scored.slice(0, limit);
}

/** Token-based highlighter (matches on normalized tokens). */
export function HighlightByToken({
  text,
  tokensToMark,
  className,
  markClassName = "bg-yellow-200 rounded px-0.5",
}: {
  text: string;
  tokensToMark: string[];
  className?: string;
  markClassName?: string;
}) {
  if (!tokensToMark.length) return (
    <span className={className}>
      {text?.toString()}
    </span>
  );

  const esc = (s: string) => s?.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const parts: React.ReactNode[] = [];
  const pattern = new RegExp(`\\b(${tokensToMark?.map(esc)?.join("|")})\\b`, "gi");

  let last = 0;
  const str = text;
  for (const m of str?.matchAll(pattern)) {
    const idx = m.index ?? 0;
    if (idx > last) parts.push(<span key={last}>
      {str?.slice(last, idx)}
                               </span>);
    parts.push(<mark
      key={`${idx}`}
      className={markClassName}
               >
      {str?.slice(idx, idx + m[0]?.length)}
               </mark>);
    last = idx + m[0].length;
  }
  if (last < str?.length) parts.push(<span key={last}>
    {str.slice(last)}
                                    </span>);

  return (
    <span className={className}>
      {parts}
    </span>
  );
}

/** Tiny TSX demo */
export default function SimilarityDemo() {
  const [q, setQ] = React.useState("Kennelwood Pet Resorts");
  const items = [
    { id: 1, name: "Kennelwood Resorts" },
    { id: 2, name: "Kennelwood Pet Resort & Spa" },
    { id: 3, name: "Pet Paradise Resort" },
    { id: 4, name: "Woodland Kennels" },
    { id: 5, name: "Random Co" },
  ];

  const results = fuzzyFilterSimilarity(items, q, (x) => x.name, { threshold: 50 });

  return (
    <div className="max-w-xl mx-auto p-4 space-y-4">
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Type a company or account…"
        className="w-full rounded-xl border px-3 py-2"
      />
      <ul className="divide-y rounded-xl border">
        {results.map(({ item, score, matchedTokens }) => (
          <li
            key={item.id}
            className="p-3 flex items-center justify-between"
          >
            <HighlightByToken
              text={item.name}
              tokensToMark={matchedTokens}
            />
            <span className="text-xs opacity-60 tabular-nums">
              score:
              {score}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
